import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Users, Truck, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";
import {
  PageHeader,
  SectionHeading,
  StatPill
} from "../components/sections/Shared";
import { COMPANY, TEAM, COVERAGE, WHY_US } from "../data/mock";

const MILESTONES = [
  {
    period: "Early 2024",
    title: "The spark & first route",
    text: "After years at FedEx and driving Uber across the Capital Region, our founder buys a single cargo van and launches 518 Courier to give Albany businesses a reliable, driver-first alternative."
  },
  {
    period: "Late 2024",
    title: "Tri-city expansion",
    text: "Expands routes into Schenectady and Troy, working with local drivers paid fair, predictable rates."
  },
  {
    period: "2025",
    title: "Medical courier service",
    text: "Adds a dedicated medical and lab specimen delivery lane for clinics, labs and pharmacies across the Capital Region."
  },
  {
    period: "2025",
    title: "24/7 human dispatch",
    text: "Establishes 24/7 local dispatch staffed by real regional operators — no chatbots, no automated call loops."
  },
  {
    period: "2026",
    title: "Interstate corridor",
    text: "Extends interstate courier service between New York and Massachusetts, Connecticut and Vermont for businesses and individuals."
  }
];

const VALUES = [
  {
    icon: Truck,
    title: "On time, every time",
    text: "We measure ourselves on minutes, not days. Every late delivery is a debrief."
  },
  {
    icon: Users,
    title: "Local people",
    text: "Every driver and dispatcher lives in the 518. We know the back roads."
  },
  {
    icon: Award,
    title: "Service like it's 1995",
    text: "You'll always reach a human. No bots, no tickets, no waiting on hold."
  }
];

export default function About() {
  return (
    <>
      <Seo
        title="About 518 Courier — Local Courier Service in Albany, NY"
        description="518 Courier is a local courier and delivery service based in Schenectady, NY, serving businesses and individuals across the Capital Region since 2024."
        path="/about"
      />
      <PageHeader
        eyebrow="About 518 Courier"
        title="Local since 2024."
        accent="Driven by the 518."
        description="518 Courier started with one van, one phone line and a promise: pick up when you call, treat drivers fairly, and get it there on time. That's still the whole pitch."
        image="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzgwMjUzNDA5fDA&ixlib=rb-4.1.0&q=85"
      />

      {/* STORY */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-518 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="img-zoom rounded-3xl overflow-hidden ring-1 ring-slate-200 shadow-xl">
              <img
                src="https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="518 Courier warehouse team"
                className="w-full h-[460px] object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-3">
              <span className="w-6 h-[2px] bg-[#f59321]" />
              Our story
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0f2847] leading-[1.1]">
              We built the courier service we wished{" "}
              <span className="text-[#f59321]">existed.</span>
            </h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed text-base md:text-lg">
              <p>
                In 2024, after years inside the logistics machine at FedEx
                learning high-volume precision and time-critical delivery,
                our founder saw the other side of the transportation industry
                while driving for Uber across the Albany area.
              </p>
              <p>
                He experienced firsthand how giant gig platforms treat both
                local drivers and customers: taking massive cuts, squeezing
                drivers with unpredictable pay on Central Avenue and Wolf Road,
                and leaving clients stuck with automated chatbots whenever a
                delivery went wrong.
              </p>
              <p>
                He knew the Capital Region deserved better — a service that
                combined FedEx-level reliability with a direct, local
                connection that actually supports local drivers.
              </p>
              <p>
                So in 2024, he bought a cargo van, printed flyers for law
                firms, medical clinics, and local businesses along Western
                Avenue, and started answering the phone himself.
              </p>
              <p>
                Today, 518 Courier connects local businesses across Albany,
                Schenectady, Troy, and Saratoga with dependable, professional
                delivery solutions. The foundation hasn't changed: pick up
                when you call, compensate drivers fairly, deliver on time,
                and treat every package like it's the only one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAND (verified only) */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="container-518 py-10 grid grid-cols-2 md:grid-cols-3 gap-8">
          <StatPill value="2024" label="Local courier launch" />
          <StatPill value="518 + NY/MA/CT/VT" label="Service coverage" />
          <StatPill value="24 / 7" label="Live dispatch" />
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-518">
          <SectionHeading
            eyebrow="What we stand for"
            title="Three rules we'll never"
            accent="break."
          />
          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <div
                key={i}
                className="card-lift bg-white border border-slate-200 rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#f59321]/10 text-[#f59321] flex items-center justify-center mx-auto mb-5">
                  <v.icon className="w-8 h-8" />
                </div>
                <h3 className="font-display font-bold text-xl text-[#0f2847] mb-2">
                  {v.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-20 md:py-24 bg-[#0f2847] text-white stripe-bg">
        <div className="container-518">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-3">
              <span className="w-6 h-[2px] bg-[#f59321]" />
              Milestones
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.1]">
              The journey so far,{" "}
              <span className="text-[#f59321]">step by step.</span>
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-[2px] bg-white/15 md:-translate-x-1/2" />
            <ul className="space-y-8">
              {MILESTONES.map((m, i) => (
                <li
                  key={i}
                  className={`relative md:grid md:grid-cols-2 md:gap-12 items-center ${
                    i % 2 === 0 ? "" : "md:[&>div:first-child]:order-2"
                  }`}
                >
                  <div className={`pl-12 md:pl-0 ${i % 2 === 0 ? "md:text-right md:pr-12" : "md:pl-12"}`}>
                    <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321]">
                      {m.period}
                    </div>
                    <div className="mt-1 font-display text-2xl md:text-3xl font-extrabold text-white leading-tight">
                      {m.title}
                    </div>
                    <p className="mt-2 text-slate-200 leading-relaxed">{m.text}</p>
                  </div>
                  <div className="absolute left-4 md:left-1/2 top-2 w-4 h-4 rounded-full bg-[#f59321] ring-4 ring-[#0f2847] md:-translate-x-1/2" />
                  <div className="hidden md:block" />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="container-518">
          <SectionHeading
            eyebrow="Who answers the phone"
            title="Meet the people behind"
            accent="518 Courier."
          />
          <div className="grid md:grid-cols-3 gap-6">
            {TEAM.map((p, i) => (
              <div
                key={i}
                className="card-lift bg-white rounded-2xl border border-slate-200 p-7"
              >
                <div className="w-20 h-20 rounded-full bg-[#0f2847] text-white font-display font-extrabold text-2xl flex items-center justify-center mb-5">
                  {p.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
                <h4 className="font-display font-bold text-xl text-[#0f2847]">{p.name}</h4>
                <div className="text-sm text-[#f59321] font-semibold mb-3">{p.role}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{p.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-518">
          <SectionHeading
            eyebrow="Where we run"
            title="Coverage across the"
            accent="Capital Region."
            description="Headquartered in Albany, with daily routes blanketing every 518 zip code."
          />
          <div className="flex flex-wrap justify-center gap-2.5 mb-10">
            {COVERAGE.map((city) => (
              <span
                key={city}
                className="px-4 py-2 rounded-full bg-slate-100 hover:bg-[#0f2847] hover:text-white text-sm font-semibold text-slate-700 transition-colors border border-slate-200"
              >
                <MapPin className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5 text-[#f59321]" />
                {city}
              </span>
            ))}
          </div>
          <div className="text-center">
            <Link to="/contact">
              <Button className="btn-lift bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-12 px-7 font-semibold">
                Outside our zone? Ask anyway
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
